// 🧠 SERVICIO DE MAPEO DE DATOS PARA AUTO-LLENADO
// ================================================
// Convierte datos extraídos del archivo a formatos de formularios EMG

import { ClinicalSymptomsData } from '../components/ClinicalSymptomsForm';
import { NCSTestResult } from '../types/ncs';
import { EMGNerveRecord } from '../components/EMGNeedleAnalysis';
import { Patient } from '../types/patient';
// ✨ NUEVO: Importar mappers seguros y tipos unificados
import { NullSafeMapperFactory, SafeExtractionResult } from './nullSafeMappers';
import type { ExtractedFileData, MappingResult } from '../types/enhancedSystem';
import { displayMuscleName, displayNerveName } from './clinicalTerminology';
import { inferSymptomsFromClinicalText } from './clinicalSymptomsExtractor';
import { birthDateFromAge, isValidPatientId, parseFullName } from '../utils/patientNameParser';

// ========== USANDO TIPOS UNIFICADOS ==========
// ExtractedFileData y MappingResult ahora vienen de types/enhancedSystem.ts

// Logger para el mapeo
class FormDataLogger {
  private logs: Array<{
    timestamp: string;
    level: 'info' | 'warn' | 'error';
    source: string;
    message: string;
    data?: any;
  }> = [];

  log(level: 'info' | 'warn' | 'error', source: string, message: string, data?: any) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      source,
      message,
      data
    };
    
    this.logs.push(logEntry);
    
    console.log(`[FormMapper:${source}] ${message}`, data || '');
  }

  getLogs() {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
  }
}

export const formLogger = new FormDataLogger();

// 🧠 CLASE PRINCIPAL DE MAPEO MEJORADA
export class FormDataMapper {
  
  // ========================================
  // 0. MAPEO PRINCIPAL INTEGRADO
  // ========================================
  static mapAllFormData(extractedData: ExtractedFileData): {
    symptoms: MappingResult<ClinicalSymptomsData>;
    ncs: MappingResult<NCSTestResult[]>;
    emg: MappingResult<EMGNerveRecord[]>;
    specialStudies: MappingResult<any>; // 🔥 AGREGAR ESTUDIOS ESPECIALES
    patient: Partial<Patient>;
    overall: {
      success: boolean;
      totalFieldsMapped: number;
      overallConfidence: number;
      warnings: string[];
      errors: string[];
    };
  } {
    formLogger.log('info', 'FormDataMapper', '🚀 Iniciando mapeo completo de datos extraídos', {
      hasPatient: !!extractedData.patient,
      hasNCS: !!extractedData.ncsResults,
      hasEMG: !!extractedData.emgResults,
      hasSymptoms: !!extractedData.symptoms
    });

    // Mapear cada sección por separado
    const symptomsResult = this.mapSymptomsData(extractedData);
    const ncsResult = this.mapNCSData(extractedData);
    const emgResult = this.mapEMGData(extractedData);
    const specialStudiesResult = this.mapSpecialStudiesData(extractedData); // 🔥 MAPEAR ESTUDIOS ESPECIALES
    const patientData = this.mapPatientData(extractedData);

    // Calcular estadísticas generales
    const totalFieldsMapped = symptomsResult.mappingStats.fieldsMapped +
                             ncsResult.mappingStats.fieldsMapped +
                             emgResult.mappingStats.fieldsMapped +
                             specialStudiesResult.mappingStats.fieldsMapped; // 🔥 INCLUIR ESTUDIOS ESPECIALES

    const overallConfidence = (
      symptomsResult.mappingStats.confidence +
      ncsResult.mappingStats.confidence +
      emgResult.mappingStats.confidence +
      specialStudiesResult.mappingStats.confidence // 🔥 INCLUIR ESTUDIOS ESPECIALES
    ) / 4;

    const allWarnings = [
      ...symptomsResult.warnings,
      ...ncsResult.warnings,
      ...emgResult.warnings,
      ...specialStudiesResult.warnings, // 🔥 INCLUIR ADVERTENCIAS DE ESTUDIOS ESPECIALES
      ...(extractedData.warnings || [])
    ];

    const allErrors = [
      ...symptomsResult.errors,
      ...ncsResult.errors,
      ...emgResult.errors,
      ...specialStudiesResult.errors // 🔥 INCLUIR ERRORES DE ESTUDIOS ESPECIALES
    ];

    const overallSuccess = symptomsResult.success && ncsResult.success && emgResult.success && specialStudiesResult.success;

    formLogger.log('info', 'FormDataMapper', '🎯 Mapeo completo finalizado', {
      totalFieldsMapped,
      overallConfidence: `${(overallConfidence * 100).toFixed(1)}%`,
      overallSuccess,
      warnings: allWarnings.length,
      errors: allErrors.length
    });

    return {
      symptoms: symptomsResult,
      ncs: ncsResult,
      emg: emgResult,
      specialStudies: specialStudiesResult, // 🔥 INCLUIR ESTUDIOS ESPECIALES EN RETORNO
      patient: patientData,
      overall: {
        success: overallSuccess,
        totalFieldsMapped,
        overallConfidence,
        warnings: allWarnings,
        errors: allErrors
      }
    };
  }

  // ========================================
  // 0.1 MAPEO DE DATOS DEL PACIENTE
  // ========================================
  static mapPatientData(extractedData: ExtractedFileData): Partial<Patient> {
    const patientData = extractedData.patient;
    if (!patientData) {
      formLogger.log('warn', 'PatientMapper', '⚠️ No hay datos de paciente para mapear');
      return {};
    }

    const fullName = patientData.name?.trim()
      || [patientData.firstName, patientData.lastName].filter(Boolean).join(' ').trim();

    const mappedPatient: Partial<Patient> = {
      id: isValidPatientId(patientData.id || '') ? patientData.id : '',
      ...parseFullName(fullName)
    };

    if (patientData.sex) {
      mappedPatient.sex = patientData.sex;
    }

    if (patientData.age && patientData.age > 0) {
      mappedPatient.age = patientData.age;
      mappedPatient.dateOfBirth = birthDateFromAge(patientData.age);
    }

    formLogger.log('info', 'PatientMapper', '✅ Datos del paciente mapeados', {
      id: mappedPatient.id,
      name: `${mappedPatient.firstName} ${mappedPatient.lastName}`,
      age: patientData.age,
      sex: mappedPatient.sex
    });

    return mappedPatient;
  }

  // ========================================
  // 1. MAPEAR SÍNTOMAS CLÍNICOS (MEJORADO CON NULL-SAFE MAPPERS)
  // ========================================
  static mapSymptomsData(extractedData: ExtractedFileData): MappingResult<ClinicalSymptomsData> {
    // Inferir síntomas desde notas clínicas si el extractor no los estructuró aún.
    if (!extractedData.symptoms && extractedData.clinicalNotes) {
      extractedData.symptoms = inferSymptomsFromClinicalText(extractedData.clinicalNotes);
    }

    formLogger.log('info', 'SymptomsMapper', '🔄 Iniciando mapeo de síntomas con mappers seguros', { 
      hasSymptoms: !!extractedData.symptoms,
      symptomsKeys: extractedData.symptoms ? Object.keys(extractedData.symptoms) : [],
      hasClinicalNotes: !!extractedData.clinicalNotes,
    });

    // 🚀 USAR EL NUEVO SISTEMA DE MAPPERS SEGUROS (PACIENTE)
    const patientMapper = NullSafeMapperFactory.createPatientMapper({
      allowPartialData: true,
      strictValidation: false
    });

    // Extraer datos usando el mapper seguro como base
    const mappingResult = patientMapper.extractPatientData(JSON.stringify(extractedData));

    // Como no tenemos mapper específico para síntomas aún, usar validación segura
    // pero mantener lógica de mapeo de síntomas tradicional mejorada
    const mapperWarnings: string[] = [...mappingResult.warnings];
    const mapperErrors: string[] = [...mappingResult.errors];
    
    // Usar confianza del mapper como base
    let mapperFieldsProcessed = mappingResult.metadata.fieldsProcessed;
    let mapperFieldsMapped = mappingResult.metadata.fieldsExtracted;

    // ==== CÓDIGO LEGACY (mantener como fallback) ====
    const warnings: string[] = [];
    const errors: string[] = [];
    let fieldsProcessed = 0;
    let fieldsMapped = 0;

    // Plantilla base de síntomas mejorada
    const createEmptySymptom = (id: string, name: string) => ({
      id,
      name,
      present: false,
      severity: 'mild' as const,
      duration: '',
      onset: 'gradual' as const,
      progression: 'stable' as const,
      location: [],
      characteristics: [],
      triggers: [],
      alleviatingFactors: [],
      associatedSymptoms: [],
      notes: ''
    });

    const symptomsData: ClinicalSymptomsData = {
      motor: {
        weakness: createEmptySymptom('weakness', 'Debilidad muscular'),
        fatigue: createEmptySymptom('fatigue', 'Fatiga'),
        cramps: createEmptySymptom('cramps', 'Calambres'),
        stiffness: createEmptySymptom('stiffness', 'Rigidez'),
        tremor: createEmptySymptom('tremor', 'Temblor'),
        fasciculations: createEmptySymptom('fasciculations', 'Fasciculaciones')
      },
      sensory: {
        numbness: createEmptySymptom('numbness', 'Entumecimiento'),
        tingling: createEmptySymptom('tingling', 'Hormigueo'),
        burning: createEmptySymptom('burning', 'Ardor'),
        pain: createEmptySymptom('pain', 'Dolor'),
        hyperalgesia: createEmptySymptom('hyperalgesia', 'Hiperalgesia'),
        allodynia: createEmptySymptom('allodynia', 'Alodinia')
      },
      autonomic: {
        sweating: createEmptySymptom('sweating', 'Alteraciones sudoración'),
        temperature: createEmptySymptom('temperature', 'Disregulación térmica'),
        skinChanges: createEmptySymptom('skinChanges', 'Cambios en la piel'),
        vasomotor: createEmptySymptom('vasomotor', 'Síntomas vasomotores')
      },
      functional: {
        walkingDifficulty: createEmptySymptom('walkingDifficulty', 'Dificultad para caminar'),
        handFunction: createEmptySymptom('handFunction', 'Disfunción manual'),
        balance: createEmptySymptom('balance', 'Alteraciones del equilibrio'),
        coordination: createEmptySymptom('coordination', 'Problemas de coordinación')
      },
      constitutional: {
        weightLoss: createEmptySymptom('weightLoss', 'Pérdida de peso'),
        sleep: createEmptySymptom('sleep', 'Alteraciones del sueño'),
        mood: createEmptySymptom('mood', 'Cambios del estado de ánimo')
      }
    };

    // Mapear datos extraídos con lógica mejorada
    if (extractedData.symptoms) {
      try {
        // Mapear debilidad con más contexto
        if (extractedData.symptoms.weakness) {
          fieldsProcessed++;
          symptomsData.motor.weakness.present = extractedData.symptoms.weakness.present;
          symptomsData.motor.weakness.severity = this.mapSeverity(extractedData.symptoms.weakness.severity);
          symptomsData.motor.weakness.notes = 'Auto-llenado desde archivo procesado';
          
          // Mapear ubicación si está disponible
          if ((extractedData.symptoms.weakness as any).location) {
            symptomsData.motor.weakness.location = Array.isArray((extractedData.symptoms.weakness as any).location) 
              ? (extractedData.symptoms.weakness as any).location 
              : [(extractedData.symptoms.weakness as any).location];
          } else if ((extractedData.symptoms.weakness as any).distribution) {
            symptomsData.motor.weakness.location = [(extractedData.symptoms.weakness as any).distribution];
          }
          
          fieldsMapped++;
          formLogger.log('info', 'SymptomsMapper', '✅ Mapeó debilidad avanzada', extractedData.symptoms.weakness);
        }

        // Mapear parestesias/hormigueos con detalles
        if (extractedData.symptoms.paresthesias) {
          fieldsProcessed++;
          symptomsData.sensory.tingling.present = extractedData.symptoms.paresthesias.present;
          symptomsData.sensory.tingling.severity = this.mapSeverity(extractedData.symptoms.paresthesias.severity);
          symptomsData.sensory.tingling.notes = 'Auto-llenado desde archivo procesado';
          
          // También activar entumecimiento si hay parestesias
          if (extractedData.symptoms.paresthesias.present) {
            symptomsData.sensory.numbness.present = true;
            symptomsData.sensory.numbness.severity = symptomsData.sensory.tingling.severity;
          }
          
          fieldsMapped++;
          formLogger.log('info', 'SymptomsMapper', '✅ Mapeó parestesias avanzadas', extractedData.symptoms.paresthesias);
        }

        // Mapear dolor con contexto
        if (extractedData.symptoms.pain) {
          fieldsProcessed++;
          symptomsData.sensory.pain.present = extractedData.symptoms.pain.present;
          if (extractedData.symptoms.pain.present) {
            symptomsData.sensory.pain.severity = 'moderate';
            // Inferir tipo de dolor basado en contexto
            if ((extractedData.symptoms.pain as any).type) {
              if ((extractedData.symptoms.pain as any).type.includes('burning') || (extractedData.symptoms.pain as any).type.includes('ardor')) {
                symptomsData.sensory.burning.present = true;
              }
            }
            fieldsMapped++;
          }
          formLogger.log('info', 'SymptomsMapper', '✅ Mapeó dolor avanzado', extractedData.symptoms.pain);
        }

        // No marcar síntomas como presentes solo por hallazgos electrofisiológicos.
        // Las correlaciones se reportan como sugerencias revisables para no inventar clínica.
        warnings.push(...this.collectClinicalCorrelationSuggestions(extractedData));

        // Mapear otros síntomas detectados automáticamente
        Object.keys(extractedData.symptoms).forEach(key => {
          if (['weakness', 'paresthesias', 'pain'].indexOf(key) === -1) {
            fieldsProcessed++;
            this.mapOtherSymptom(key, extractedData.symptoms![key], symptomsData);
            formLogger.log('warn', 'SymptomsMapper', `⚠️ Síntoma adicional mapeado: ${key}`, extractedData.symptoms![key]);
          }
        });

      } catch (error) {
        const errorMsg = `Error mapeando síntomas: ${error instanceof Error ? error.message : 'Error desconocido'}`;
        formLogger.log('error', 'SymptomsMapper', errorMsg, error);
        errors.push(errorMsg);
      }
    } else {
      formLogger.log('warn', 'SymptomsMapper', '⚠️ No se encontraron datos de síntomas para mapear');
      warnings.push('No se encontraron datos de síntomas en el archivo');
    }

    const confidence = fieldsProcessed > 0 ? (fieldsMapped / fieldsProcessed) * 100 : 0;

    formLogger.log('info', 'SymptomsMapper', '🎯 Mapeo de síntomas completado', {
      fieldsProcessed,
      fieldsMapped,
      confidence: `${confidence.toFixed(1)}%`,
      warnings: warnings.length,
      errors: errors.length
    });

    // 🚀 COMBINAR RESULTADOS DEL MAPPER SEGURO CON LÓGICA TRADICIONAL
    const combinedWarnings = [...mapperWarnings, ...warnings];
    const combinedErrors = [...mapperErrors, ...errors];
    const totalFieldsProcessed = mapperFieldsProcessed + fieldsProcessed;
    const totalFieldsMapped = mapperFieldsMapped + fieldsMapped;
    const combinedConfidence = totalFieldsProcessed > 0 
      ? (totalFieldsMapped / totalFieldsProcessed) * 100 
      : 0;

    formLogger.log('info', 'SymptomsMapper', '✅ Mapeo híbrido completado', {
      mapperResults: { processed: mapperFieldsProcessed, mapped: mapperFieldsMapped },
      legacyResults: { processed: fieldsProcessed, mapped: fieldsMapped },
      totalFields: totalFieldsMapped,
      confidence: `${combinedConfidence.toFixed(1)}%`,
      warnings: combinedWarnings.length,
      errors: combinedErrors.length
    });

    return {
      data: symptomsData,
      success: combinedErrors.length === 0,
      warnings: combinedWarnings,
      errors: combinedErrors,
      mappingStats: {
        fieldsProcessed: totalFieldsProcessed,
        fieldsMapped: totalFieldsMapped,
        fieldsSkipped: totalFieldsProcessed - totalFieldsMapped,
        confidence: combinedConfidence / 100
      }
    };
  }

  // ========================================
  // 2. MAPEAR DATOS NCS (MEJORADO)
  // ========================================
  static mapNCSData(extractedData: ExtractedFileData): MappingResult<NCSTestResult[]> {
    formLogger.log('info', 'NCSMapper', '🔄 Iniciando mapeo de datos NCS mejorado', {
      hasNCSResults: !!extractedData.ncsResults,
      ncsCount: extractedData.ncsResults?.length || 0
    });

    const warnings: string[] = [];
    const errors: string[] = [];
    let fieldsProcessed = 0;
    let fieldsMapped = 0;

    const ncsResults: NCSTestResult[] = [];

    if (extractedData.ncsResults && extractedData.ncsResults.length > 0) {
      extractedData.ncsResults.forEach((ncs, index) => {
        try {
          fieldsProcessed++;
          
          const hasNarrativeSource = ncs.extractionSource?.parser === 'narrative';
          const hasQualitativeEvidence = !!ncs.absentResponse || !!ncs.qualitativeFindings?.length || (hasNarrativeSource && !!ncs.findings?.length);
          const hasNumericEvidence = ncs.latency > 0 || ncs.amplitude > 0 || ncs.velocity > 0 || (ncs.peakLatency || 0) > 0;

          if (!hasNumericEvidence && !hasQualitativeEvidence && !hasNarrativeSource) {
            const warning = `NCS ${index}: Registro vacío descartado (nerve: ${ncs.nerve}, side: ${ncs.side})`;
            formLogger.log('warn', 'NCSMapper', warning, ncs);
            warnings.push(warning);
            return;
          }

          // ✅ VALIDAR DATOS REALES - NO INVENTAR. Permitir hallazgos cualitativos auditables.
          if (!ncs.nerve || !ncs.side || (!hasNumericEvidence && !hasQualitativeEvidence && !hasNarrativeSource)) {
            const error = `NCS ${index}: Datos insuficientes (nerve: ${ncs.nerve}, side: ${ncs.side}, latency: ${ncs.latency}, amplitude: ${ncs.amplitude}, velocity: ${ncs.velocity})`;
            formLogger.log('error', 'NCSMapper', error, ncs);
            errors.push(error);
            return;
          }

          // Validar rangos clínicos con más precisión
          if (hasNumericEvidence) {
            const validationResult = this.validateNCSRanges(ncs);
            if (validationResult.warnings.length > 0) {
              warnings.push(...validationResult.warnings);
            }
          }

          // Determinar tipo de test más inteligentemente
          const testType = this.inferTestTypeAdvanced(ncs.nerve, ncs);

          // Determinar estado basado en valores
          const mappedStatus = ncs.absentResponse
            ? 'abnormal'
            : hasNumericEvidence
              ? this.determineNCSStatus(ncs, testType)
              : (ncs.status || 'undetermined');

          const mappedNCS: NCSTestResult = {
            id: crypto.randomUUID(),
            nerve: this.mapNerveName(ncs.nerve),
            side: ncs.side,
            type: testType,
            latency: ncs.latency,
            peakLatency: ncs.peakLatency,
            amplitude: ncs.amplitude,
            velocity: ncs.velocity,
            studyMode: ncs.studyMode,
            distance: ncs.distance,
            distanceUnit: ncs.distanceUnit,
            stimulationSite: ncs.stimulationSite as NCSTestResult['stimulationSite'],
            recordingSite: ncs.recordingSite,
            segmentName: ncs.segmentName,
            latencyDifference: ncs.latencyDifference,
            proximalLatency: ncs.proximalLatency,
            proximalAmplitude: ncs.proximalAmplitude,
            proximalStimulationSite: ncs.proximalStimulationSite as NCSTestResult['proximalStimulationSite'],
            segmentDistance: ncs.segmentDistance,
            segmentDistanceUnit: ncs.segmentDistanceUnit,
            segmentVelocity: ncs.segmentVelocity,
            reportedSegmentVelocity: ncs.reportedSegmentVelocity,
            status: mappedStatus,
            findings: ncs.findings || ncs.qualitativeFindings || (
              mappedStatus === 'undetermined'
                ? ['Hallazgo cualitativo requiere revisión médica.']
                : this.generateNCSFindings(ncs, mappedStatus)
            ),
            qualitativeFindings: ncs.qualitativeFindings,
            absentResponse: ncs.absentResponse,
            requiresReview: ncs.requiresReview || !hasNumericEvidence,
            fieldConfidence: ncs.fieldConfidence as NCSTestResult['fieldConfidence'],
            extractionSource: ncs.extractionSource
          };

          ncsResults.push(mappedNCS);
          fieldsMapped++;

          formLogger.log('info', 'NCSMapper', `✅ Mapeó NCS ${index + 1}: ${ncs.nerve}`, {
            nerve: mappedNCS.nerve,
            type: mappedNCS.type,
            latency: mappedNCS.latency,
            amplitude: mappedNCS.amplitude,
            velocity: mappedNCS.velocity,
            status: mappedNCS.status
          });

        } catch (error) {
          const errorMsg = `Error mapeando NCS ${index}: ${error instanceof Error ? error.message : 'Error desconocido'}`;
          formLogger.log('error', 'NCSMapper', errorMsg, { ncs, error });
          errors.push(errorMsg);
        }
      });
    } else {
      formLogger.log('warn', 'NCSMapper', '⚠️ No se encontraron datos NCS para mapear');
      warnings.push('No se encontraron datos de neuroconducción en el archivo');
    }

    const confidence = fieldsProcessed > 0 ? (fieldsMapped / fieldsProcessed) * 100 : 0;

    formLogger.log('info', 'NCSMapper', '🎯 Mapeo NCS completado', {
      fieldsProcessed,
      fieldsMapped,
      confidence: `${confidence.toFixed(1)}%`,
      warnings: warnings.length,
      errors: errors.length
    });

    return {
      data: ncsResults,
      success: errors.length === 0,
      warnings,
      errors,
      mappingStats: {
        fieldsProcessed,
        fieldsMapped,
        fieldsSkipped: fieldsProcessed - fieldsMapped,
        confidence: confidence / 100
      }
    };
  }

  // ========================================
  // 3. MAPEAR DATOS EMG (MEJORADO)
  // ========================================
  static mapEMGData(extractedData: ExtractedFileData): MappingResult<EMGNerveRecord[]> {
    formLogger.log('info', 'EMGMapper', '🔄 Iniciando mapeo de datos EMG mejorado', {
      hasEMGResults: !!extractedData.emgResults,
      emgCount: extractedData.emgResults?.length || 0
    });

    const warnings: string[] = [];
    const errors: string[] = [];
    let fieldsProcessed = 0;
    let fieldsMapped = 0;

    const emgResults: EMGNerveRecord[] = [];

    if (extractedData.emgResults && extractedData.emgResults.length > 0) {
      extractedData.emgResults.forEach((emg, index) => {
        try {
          fieldsProcessed++;
          const muscleName = emg.muscle || (emg as any).muscleOrNerveName;

          // ✅ VALIDAR DATOS REALES - NO INVENTAR
          if (!muscleName || !emg.side) {
            const error = `EMG ${index}: Datos insuficientes (muscle: ${muscleName}, side: ${emg.side})`;
            formLogger.log('error', 'EMGMapper', error, emg);
            errors.push(error);
            return;
          }

          // Validar rangos EMG
          const validationResult = this.validateEMGRanges(emg);
          if (validationResult.warnings.length > 0) {
            warnings.push(...validationResult.warnings);
          }

          // ✅ MAPEAR SOLO DATOS REALES
          const mappedEMG: EMGNerveRecord = {
            id: `emg_${index}_${Date.now()}`,
            muscleOrNerveName: this.mapMuscleName(muscleName),
            side: emg.side,
            insertionalActivity: this.mapInsertionalActivity(emg.insertionalActivity),
            spontaneousActivity: this.mapSpontaneousActivityReal(emg),
            motorUnitPotentials: this.mapMotorUnitPotentialsReal(emg),
            recruitmentPattern: this.mapRecruitmentPattern(
              emg.recruitmentPattern,
              (emg as any).maxVolitionalActivity?.pattern
            ),
            maxVolitionalActivity: this.mapMaxVolitionalActivityReal(emg),
            rawTableRow: (emg as any).rawTableRow,
            extractionSource: (emg as any).extractionSource,
            interpretationNotes: (emg as any).interpretationNotes || 'Auto-llenado desde archivo procesado'
          };

          emgResults.push(mappedEMG);
          fieldsMapped++;

          formLogger.log('info', 'EMGMapper', `✅ Mapeó EMG ${index + 1}: ${muscleName}`, {
            muscle: mappedEMG.muscleOrNerveName,
            insertionalActivity: mappedEMG.insertionalActivity,
            recruitmentPattern: mappedEMG.recruitmentPattern,
            spontaneousActivity: mappedEMG.spontaneousActivity
          });

        } catch (error) {
          const errorMsg = `Error mapeando EMG ${index}: ${error instanceof Error ? error.message : 'Error desconocido'}`;
          formLogger.log('error', 'EMGMapper', errorMsg, { emg, error });
          errors.push(errorMsg);
        }
      });
    } else {
      formLogger.log('warn', 'EMGMapper', '⚠️ No se encontraron datos EMG para mapear');
      warnings.push('No se encontraron datos de electromiografía en el archivo');
    }

    const confidence = fieldsProcessed > 0 ? (fieldsMapped / fieldsProcessed) * 100 : 0;

    formLogger.log('info', 'EMGMapper', '🎯 Mapeo EMG completado', {
      fieldsProcessed,
      fieldsMapped,
      confidence: `${confidence.toFixed(1)}%`,
      warnings: warnings.length,
      errors: errors.length
    });

    return {
      data: emgResults,
      success: errors.length === 0,
      warnings,
      errors,
      mappingStats: {
        fieldsProcessed,
        fieldsMapped,
        fieldsSkipped: fieldsProcessed - fieldsMapped,
        confidence: confidence / 100
      }
    };
  }

  // ========================================
  // 4. MAPEAR ESTUDIOS ESPECIALES (MEJORADO - Solo datos reales)
  // ========================================
  static mapSpecialStudiesData(extractedData: ExtractedFileData): MappingResult<any> {
    formLogger.log('info', 'SpecialStudiesMapper', '🔄 Iniciando mapeo de estudios especiales REALES', {
      hasSpecialStudies: !!(extractedData as any).specialStudies,
      extractedKeys: Object.keys(extractedData)
    });

    const warnings: string[] = [];
    const errors: string[] = [];
    let fieldsProcessed = 0;
    let fieldsMapped = 0;

    // Estructura por defecto para estudios especiales
    const specialStudiesData = {
      notPerformed: true,
      tests: [] as any[]
    };

    // 🔍 SOLO BUSCAR ESTUDIOS ESPECIALES REALES EN LOS DATOS EXTRAÍDOS
    const specialStudiesFromFile = (extractedData as any).specialStudies;
    
    if (specialStudiesFromFile && Array.isArray(specialStudiesFromFile) && specialStudiesFromFile.length > 0) {
      try {
        const trustedStudies = specialStudiesFromFile.filter((study: any) => {
          const source = study.extractionSource || {};
          const sourceSection = String(source.sourceSection || '').toLowerCase();
          const hasStructuredSource = source.parser === 'table' || source.tableIndex !== undefined;
          const hasNarrativeSource = source.parser === 'narrative' && sourceSection.includes('special_studies_narrative');
          const type = String(study.type || '').toLowerCase();
          const sectionMatchesType =
            (type === 'h_reflex' && sourceSection.includes('h_reflex')) ||
            (type === 'f_wave' && sourceSection.includes('f_wave')) ||
            (type === 'blink_reflex' && sourceSection.includes('blink')) ||
            (type === 'rns' && sourceSection.includes('rns')) ||
            (['h_reflex', 'f_wave', 'rns', 'other'].includes(type) && sourceSection.includes('special_studies_narrative'));
          const hasQualitativeEvidence = !!(
            study.absentResponse ||
            study.values?.absentResponse ||
            study.qualitativeFindings?.length ||
            study.findings?.length
          );
          const supportsQualitativeEvidence = type === 'h_reflex' || type === 'f_wave';

          return (hasStructuredSource && sectionMatchesType && this.hasPhysiologicSpecialStudyValues(type, study.values || {})) ||
            (sectionMatchesType && supportsQualitativeEvidence && hasQualitativeEvidence && (hasStructuredSource || hasNarrativeSource));
        });

        if (trustedStudies.length === 0) {
          formLogger.log('warn', 'SpecialStudiesMapper', '⚠️ Estudios especiales descartados por falta de fuente estructurada confiable', {
            received: specialStudiesFromFile.length
          });
          warnings.push('Se descartaron estudios especiales sin tabla/fuente estructurada compatible.');
          return {
            data: specialStudiesData,
            success: true,
            warnings,
            errors,
            mappingStats: {
              fieldsProcessed: specialStudiesFromFile.length,
              fieldsMapped: 0,
              fieldsSkipped: specialStudiesFromFile.length,
              confidence: 0
            }
          };
        }

        fieldsProcessed++;
        
        // Mapear solo los estudios especiales que realmente aparecen en el documento.
        // Se deduplican entradas idénticas (mismo tipo/lado/valores) para evitar la
        // explosión de filas repetidas observada en extracciones previas.
        specialStudiesData.notPerformed = false;
        const seenStudyKeys = new Set<string>();
        specialStudiesData.tests = trustedStudies.reduce((acc: any[], study: any, index: number) => {
          const sanitizedValues = this.sanitizeSpecialStudyValues(String(study.type || 'other'), study.values || {});
          const latencyKey = sanitizedValues.latency != null ? Number(sanitizedValues.latency).toFixed(1) : '-';
          const amplitudeKey = sanitizedValues.amplitude != null ? Number(sanitizedValues.amplitude).toFixed(1) : '-';
          const dedupeKey = `${study.type || 'other'}|${study.side || 'bilateral'}|${study.nerve || ''}|${latencyKey}|${amplitudeKey}|${!!sanitizedValues.absentResponse}`;

          if (seenStudyKeys.has(dedupeKey)) {
            return acc;
          }
          seenStudyKeys.add(dedupeKey);

          const mappedStudy = {
            id: study.id || `special_study_${index}`,
            name: study.name || 'Estudio Especial',
            type: study.type || 'other',
            side: study.side || 'bilateral',
            values: sanitizedValues,
            status: study.status || 'normal',
            findings: study.findings || [],
            notes: study.notes || 'Extraído del documento original'
          };
          
          formLogger.log('info', 'SpecialStudiesMapper', `✅ Mapeando estudio real: ${mappedStudy.name}`, {
            type: mappedStudy.type,
            values: mappedStudy.values,
            status: mappedStudy.status
          });
          
          acc.push(mappedStudy);
          return acc;
        }, []);
        
        fieldsMapped++;
        
        formLogger.log('info', 'SpecialStudiesMapper', `✅ Mapeó ${specialStudiesData.tests.length} estudios especiales REALES del documento`, specialStudiesData.tests);
        
      } catch (error) {
        const errorMsg = `Error mapeando estudios especiales: ${error instanceof Error ? error.message : 'Error desconocido'}`;
        formLogger.log('error', 'SpecialStudiesMapper', errorMsg, error);
        errors.push(errorMsg);
      }
    } else {
      // 🚫 NO INVENTAR ESTUDIOS - Solo reportar que no se encontraron
      formLogger.log('warn', 'SpecialStudiesMapper', '⚠️ No se encontraron estudios especiales en el documento');
      warnings.push('No se encontraron estudios especiales en el archivo. Los estudios especiales se dejarán como "No realizados".');
    }

    const confidence = fieldsProcessed > 0 ? (fieldsMapped / fieldsProcessed) * 100 : 0;

    formLogger.log('info', 'SpecialStudiesMapper', '🎯 Mapeo de estudios especiales completado', {
      fieldsProcessed,
      fieldsMapped,
      confidence: `${confidence.toFixed(1)}%`,
      warnings: warnings.length,
      errors: errors.length,
      notPerformed: specialStudiesData.notPerformed,
      testsCount: specialStudiesData.tests.length,
      policy: 'SOLO_DATOS_REALES_NO_INVENTAR'
    });

    return {
      data: specialStudiesData,
      success: errors.length === 0,
      warnings,
      errors,
      mappingStats: {
        fieldsProcessed,
        fieldsMapped,
        fieldsSkipped: fieldsProcessed - fieldsMapped,
        confidence: confidence / 100
      }
    };
  }

  // ========================================
  // FUNCIONES AUXILIARES MEJORADAS
  // ========================================

  private static mapSeverity(severity: string): 'mild' | 'moderate' | 'severe' {
    const normalizedSeverity = severity?.toLowerCase() || 'mild';
    if (normalizedSeverity.includes('severe') || normalizedSeverity.includes('severo') || normalizedSeverity.includes('grave')) return 'severe';
    if (normalizedSeverity.includes('moderate') || normalizedSeverity.includes('moderado')) return 'moderate';
    return 'mild';
  }

  // Elimina valores numéricos imposibles (p. ej. latencias corruptas tipo 60420260623
  // generadas por concatenación de celdas) antes de que lleguen al formulario clínico.
  // Conserva la evidencia cualitativa (absentResponse) aunque se descarten los números.
  private static sanitizeSpecialStudyValues(type: string, values: any): any {
    const ranges: Record<string, { latency: [number, number]; amplitude: number }> = {
      h_reflex: { latency: [15, 50], amplitude: 20 },
      f_wave: { latency: [15, 80], amplitude: 20 },
      blink_reflex: { latency: [5, 50], amplitude: 20 },
      rns: { latency: [0, 100], amplitude: 100 }
    };

    const sanitized: any = { ...values };
    const range = ranges[type];

    const rawLatency = Number(values.latency);
    if (values.latency != null) {
      const latencyValid = Number.isFinite(rawLatency) && rawLatency > 0 &&
        (!range || (rawLatency >= range.latency[0] && rawLatency <= range.latency[1]));
      if (!latencyValid) {
        delete sanitized.latency;
        sanitized.requiresReview = true;
      }
    }

    const rawAmplitude = Number(values.amplitude);
    if (values.amplitude != null) {
      const amplitudeValid = Number.isFinite(rawAmplitude) && rawAmplitude > 0 &&
        (!range || rawAmplitude <= range.amplitude);
      if (!amplitudeValid) {
        delete sanitized.amplitude;
        sanitized.requiresReview = true;
      }
    }

    return sanitized;
  }

  private static hasPhysiologicSpecialStudyValues(type: string, values: any): boolean {
    const latency = Number(values.latency || 0);
    const amplitude = Number(values.amplitude || 0);

    if (type === 'h_reflex') {
      return latency >= 15 && latency <= 50 && (!amplitude || amplitude <= 20);
    }
    if (type === 'f_wave') {
      return latency >= 15 && latency <= 80 && (!amplitude || amplitude <= 20);
    }
    if (type === 'blink_reflex') {
      return latency >= 5 && latency <= 50;
    }
    if (type === 'rns') {
      return latency > 0 || amplitude > 0 || Number(values.decrementPercentage || 0) > 0;
    }

    return false;
  }

  private static collectClinicalCorrelationSuggestions(extractedData: ExtractedFileData): string[] {
    const suggestions: string[] = [];

    if (extractedData.ncsResults) {
      const hasAbnormalNCS = extractedData.ncsResults.some(ncs => ncs.status === 'abnormal');
      if (hasAbnormalNCS) {
        suggestions.push('NCS anormal: confirmar síntomas sensitivos/motores en interrogatorio antes de marcarlos como presentes.');
      }
    }

    if (extractedData.emgResults) {
      const hasAbnormalEMG = extractedData.emgResults.some(emg => 
        emg.spontaneousActivity?.fibrillations || emg.spontaneousActivity?.positiveWaves
      );
      if (hasAbnormalEMG) {
        suggestions.push('EMG con actividad espontánea: confirmar debilidad u otros síntomas motores antes de auto-llenarlos.');
      }
    }

    return suggestions;
  }

  private static mapOtherSymptom(key: string, value: any, symptomsData: ClinicalSymptomsData) {
    const normalizedKey = key.toLowerCase();
    const symptomMap: { [key: string]: string } = {
      'fatigue': 'motor.fatigue',
      'cramps': 'motor.cramps',
      'tremor': 'motor.tremor',
      'burning': 'sensory.burning',
      'stiffness': 'motor.stiffness',
      'fasciculations': 'motor.fasciculations',
      'walkingdifficulty': 'functional.walkingDifficulty',
    };

    const mappedPath = symptomMap[normalizedKey];
    if (mappedPath) {
      const [category, symptom] = mappedPath.split('.');
      const section = symptomsData[category as keyof ClinicalSymptomsData] as Record<string, { present?: boolean; severity?: string; notes?: string }>;
      if (section?.[symptom]) {
        section[symptom].present = value.present ?? true;
        if (value.severity) {
          section[symptom].severity = this.mapSeverity(value.severity);
        }
        section[symptom].notes = 'Auto-llenado desde archivo procesado';
      }
    }
  }

  private static inferTestTypeAdvanced(nerve: string, ncs: any): 'motor' | 'sensory' {
    // Lógica avanzada para determinar tipo de test
    if (ncs.type) return ncs.type;
    
    // Basado en amplitud (motores típicamente > sensitivos)
    if (ncs.amplitude > 5) return 'motor';
    if (ncs.amplitude < 2) return 'sensory';
    
    // Basado en nervio
    const motorNerves = ['mediano motor', 'ulnar motor', 'radial motor'];
    const normalized = nerve.toLowerCase();
    return motorNerves.some(mn => normalized.includes(mn.split(' ')[0])) ? 'motor' : 'sensory';
  }

  private static determineNCSStatus(ncs: any, type: 'motor' | 'sensory'): 'normal' | 'abnormal' {
    if (ncs.status === 'undetermined') return 'abnormal';

    // Rangos fisiológicos amplios para evitar falsos positivos por tablas segmentarias.
    // Los umbrales diagnósticos finos viven en nerveData/emgClinicalCriteria.
    const normalRanges = {
      motor: { latency: [1, 6.5], amplitude: [2, 50], velocity: [40, 80] },
      sensory: { latency: [0.8, 4.5], amplitude: [6, 200], velocity: [40, 120] }
    };
    
    const ranges = normalRanges[type];
    const latencyForStatus = type === 'sensory'
      ? (ncs.peakLatency || ncs.latency)
      : ncs.latency;
    const velocityForStatus = ncs.reportedSegmentVelocity || ncs.segmentVelocity || ncs.velocity;

    if ((latencyForStatus > 0 && latencyForStatus > ranges.latency[1]) ||
        (ncs.amplitude > 0 && ncs.amplitude < ranges.amplitude[0]) ||
        (velocityForStatus > 0 && velocityForStatus < ranges.velocity[0])) {
      return 'abnormal';
    }
    
    return 'normal';
  }

  private static generateNCSFindings(ncs: any, status: 'normal' | 'abnormal'): string[] {
    const findings: string[] = [];
    
    if (status === 'abnormal') {
      const latencyLimit = ncs.type === 'sensory' ? 4.5 : 6.5;
      const amplitudeLimit = ncs.type === 'sensory' ? 6 : 2;
      const velocityLimit = ncs.type === 'sensory' ? 40 : 40;
      const latencyForFinding = ncs.type === 'sensory' ? (ncs.peakLatency || ncs.latency) : ncs.latency;
      const velocityForFinding = ncs.reportedSegmentVelocity || ncs.segmentVelocity || ncs.velocity;
      if (latencyForFinding > latencyLimit) findings.push('Latencia prolongada');
      if (ncs.amplitude > 0 && ncs.amplitude < amplitudeLimit) findings.push('Amplitud reducida');
      if (velocityForFinding > 0 && velocityForFinding < velocityLimit) findings.push('Velocidad de conducción lenta');
    }
    
    return findings;
  }

  private static mapSpontaneousActivityReal(emg: any): any {
    if (!emg.spontaneousActivity) {
      return {
        fibrillations: 0,
        positiveWaves: 0,
        fasciculations: false
      };
    }
    
    return {
      fibrillations: this.mapActivityGrade(emg.spontaneousActivity.fibrillations),
      positiveWaves: this.mapActivityGrade(emg.spontaneousActivity.positiveWaves),
      fasciculations: emg.spontaneousActivity.fasciculations || false
    };
  }

  private static mapMotorUnitPotentialsReal(emg: any): any {
    if (!emg.motorUnitPotentials) {
      return {
        amplitude: 0,
        duration: 0,
        polyphasia: 0
      };
    }
    
    return {
      amplitude: emg.motorUnitPotentials.amplitude || 0,
      duration: emg.motorUnitPotentials.duration || 0,
      polyphasia: emg.motorUnitPotentials.polyphasia || 0,
      amplitudeLabel: emg.motorUnitPotentials.amplitudeLabel || '',
      durationLabel: emg.motorUnitPotentials.durationLabel || '',
      polyphasiaLabel: emg.motorUnitPotentials.polyphasiaLabel || '',
      configuration: emg.motorUnitPotentials.configuration || '',
      phases: emg.motorUnitPotentials.phases,
      turns: emg.motorUnitPotentials.turns,
      riseTimeUs: emg.motorUnitPotentials.riseTimeUs
    };
  }

  private static mapActivityGrade(value: any): 0 | 1 | 2 | 3 | 4 {
    if (value === true) return 1;
    if (!value) return 0;
    if (typeof value === 'number') return Math.max(0, Math.min(4, value)) as 0 | 1 | 2 | 3 | 4;
    const normalized = String(value).toLowerCase();
    const gradeMatch = normalized.match(/([1-4])\s*\+|\+{1,4}/);
    if (gradeMatch?.[1]) return Math.max(0, Math.min(4, Number(gradeMatch[1]))) as 0 | 1 | 2 | 3 | 4;
    if (gradeMatch) return Math.max(0, Math.min(4, gradeMatch[0].replace(/\s/g, '').length)) as 0 | 1 | 2 | 3 | 4;
    if (/(present|presente|few|trace|rare)/.test(normalized)) return 1;
    return 0;
  }

  private static mapMaxVolitionalActivityReal(emg: any): any {
    const value = emg.maxVolitionalActivity;
    if (!value) return undefined;
    return {
      amplitude: value.amplitude || '',
      pattern: value.pattern || '',
      effort: value.effort || ''
    };
  }

  private static mapInsertionalActivity(activity: string): "" | "normal" | "increased" | "decreased" | "absent" {
    if (!activity) return "";
    
    const normalized = activity.toLowerCase();
    if (normalized.includes('normal')) return "normal";
    if (normalized.includes('aumentad') || normalized.includes('increased')) return "increased";
    if (normalized.includes('disminuid') || normalized.includes('decreased')) return "decreased";
    if (normalized.includes('ausente') || normalized.includes('absent')) return "absent";
    
    return "";
  }

  private static mapRecruitmentPattern(
    pattern: string,
    maxVolitionalPattern?: string
  ): "" | "normal" | "reduced_incomplete" | "reduced_complete" | "early" | "discrete" {
    const maxPattern = String(maxVolitionalPattern || '').toLowerCase();
    if (maxPattern.includes('discrete') || maxPattern.includes('absent')) return 'discrete';
    if (maxPattern.includes('reduced')) return 'reduced_incomplete';
    if (maxPattern.includes('early')) return 'early';

    if (!pattern) return "";
    
    const normalized = pattern.toLowerCase();
    if (normalized.includes('normal')) return "normal";
    if (normalized.includes('reducido incompleto') || normalized.includes('reduced_incomplete')) return "reduced_incomplete";
    if (normalized.includes('reducido completo') || normalized.includes('reduced_complete')) return "reduced_complete";
    if (normalized.includes('temprano') || normalized.includes('early')) return "early";
    if (normalized.includes('discreto') || normalized.includes('discrete')) return "discrete";
    
    return "";
  }

  // ========================================
  // FUNCIONES AUXILIARES
  // ========================================

  private static mapNerveName(nerve: string): string {
    // 🎯 MAPEO EXPANDIDO DE NERVIOS PARA ARCHIVOS RTF
    const nerveMap: { [key: string]: string } = {
      // Nervios principales del brazo
      'median': 'Mediano',
      'mediano': 'Mediano',
      'median motor': 'Mediano',
      'median sensory': 'Mediano',
      'median-wrist': 'Mediano',
      'median-elbow': 'Mediano',
      
      'ulnar': 'Ulnar',
      'cubital': 'Ulnar',
      'ulnar motor': 'Ulnar',
      'ulnar sensory': 'Ulnar',
      'ulnar-wrist': 'Ulnar',
      'ulnar-elbow': 'Ulnar',
      
      'radial': 'Radial',
      'radial motor': 'Radial',
      'radial sensory': 'Radial',
      'radial-spiral groove': 'Radial',
      
      // Nervios menos comunes que aparecen en RTF
      'musculocutaneous': 'Musculocutáneo',
      'musculocutaneo': 'Musculocutáneo',
      'musculocutaneous sensory': 'Musculocutáneo',
      
      'axillary': 'Axilar',
      'axilar': 'Axilar',
      
      'suprascapular': 'Supraescapular',
      'supraescapular': 'Supraescapular',
      
      // Nervios de la pierna
      'peroneal': 'Peroneo',
      'peroneo': 'Peroneo',
      'peroneal deep': 'Peroneo Profundo',
      'peroneal superficial': 'Peroneo Superficial',
      
      'tibial': 'Tibial',
      'tibial motor': 'Tibial',
      'tibial sensory': 'Tibial',
      
      'sural': 'Sural',
      'sural sensory': 'Sural',
      
      'femoral': 'Femoral'
    };
    
    const normalized = nerve.toLowerCase().trim()
      .replace(/\s+/g, ' ') // Normalizar espacios
      .replace(/[-_]/g, ' '); // Convertir guiones y guiones bajos a espacios
    
    // Buscar coincidencia exacta primero
    if (nerveMap[normalized]) {
      return nerveMap[normalized];
    }
    
    // Buscar coincidencia parcial para nervios con especificaciones
    for (const [key, value] of Object.entries(nerveMap)) {
      if (normalized.includes(key.split(' ')[0]) && key.includes(' ')) {
        // Si encontramos el nervio base, pero con especificación (motor/sensory)
        return value;
      }
    }
    
    // Buscar por palabra clave principal
    const keywords = ['median', 'ulnar', 'radial', 'musculocutaneous', 'tibial', 'peroneal', 'sural'];
    for (const keyword of keywords) {
      if (normalized.includes(keyword)) {
        const mapped = nerveMap[keyword];
        if (mapped) return mapped;
      }
    }
    
    // 🔧 LIMPIEZA Y CAPITALIZACIÓN INTELIGENTE
    // Si no se encuentra en el mapeo, limpiar y capitalizar
    const cleaned = nerve.trim()
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
      
    return displayNerveName(cleaned);
  }

  private static mapMuscleName(muscle: string): string {
    // 🎯 MAPEO EXPANDIDO DE MÚSCULOS PARA ARCHIVOS RTF
    const muscleMap: { [key: string]: string } = {
      // Músculos del hombro y brazo
      'deltoid': 'Deltoid',
      'supraspinatus': 'Supraspinatus',
      'biceps brachi.': 'Biceps Brachii',
      'biceps': 'Biceps Brachii',
      'triceps': 'Triceps',
      
      // Músculos del antebrazo
      'pronator teres': 'Pronator Teres',
      'pronator ter.': 'Pronator Teres',
      'flexor carpi radialis': 'Flexor Carpi Radialis',
      'flexor carpi ulnaris': 'Flexor Carpi Ulnaris',
      'flx.car.uln': 'Flexor Carpi Ulnaris',
      'extensor indicis proprius': 'Extensor Indicis Proprius',
      'ext.ind.pro.': 'Extensor Indicis Proprius',
      'extensor indicis': 'Extensor Indicis Proprius',
      
      // Músculos de la mano
      'abductor pollicis brevis': 'Abductor Pollicis Brevis',
      'abd.pol.br.': 'Abductor Pollicis Brevis',
      'first dorsal interosseous': '1er Interóseo Dorsal',
      'dors.int.1': '1er Interóseo Dorsal',
      '1st dors int': '1er Interóseo Dorsal',
      
      // Músculos paraespinales
      'paraspinals': 'Paraspinals',
      'parasp': 'Paraspinals',
      'paraspinals c6': 'Paraspinals C6',
      'paraspinals c7': 'Paraspinals C7',
      'paraspinals c8': 'Paraspinals C8',
      
      // Músculos de la pierna (para casos futuros)
      'tibialis anterior': 'Tibialis Anterior',
      'gastrocnemius': 'Gastrocnemius',
      'peroneus longus': 'Peroneus Longus',
      'biceps femoris (short head)': 'Biceps Femoris (Short Head)',
      'biceps femoris': 'Biceps Femoris',
      'semitendinosus': 'Semitendinosus',
      'vastus lateralis': 'Vastus Lateralis',
      'vastus lat.': 'Vastus Lateralis',
      
      // Variaciones comunes en RTF
      'bicep': 'Biceps Brachii',
      'tricep': 'Triceps'
    };
    
    const normalized = muscle.toLowerCase().trim();
    
    // Buscar coincidencia exacta primero
    if (muscleMap[normalized]) {
      return muscleMap[normalized];
    }
    
    // Buscar coincidencia parcial (útil para abreviaciones)
    for (const [key, value] of Object.entries(muscleMap)) {
      const allowPartial = key.includes('.') || key.length >= 8;
      if (allowPartial && (normalized.includes(key) || key.includes(normalized))) {
        return value;
      }
    }
    
    // 🔧 LIMPIEZA Y CAPITALIZACIÓN INTELIGENTE
    // Si no se encuentra en el mapeo, limpiar y capitalizar
    const cleaned = muscle.trim()
      .replace(/\.$/, '') // Remover punto final
      .replace(/\s+/g, ' ') // Normalizar espacios
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
      
    return displayMuscleName(cleaned);
  }

  private static validateNCSRanges(ncs: any): { warnings: string[] } {
    const warnings: string[] = [];
    
    // Validar latencia (1-50ms normal)
    if (ncs.latency && (ncs.latency < 1 || ncs.latency > 50)) {
      warnings.push(`Latencia fuera de rango normal: ${ncs.latency}ms (rango: 1-50ms)`);
    }
    
    // Validar amplitud motor (0.1-50mV normal)
    if (ncs.amplitude && (ncs.amplitude < 0.1 || ncs.amplitude > 50)) {
      warnings.push(`Amplitud fuera de rango normal: ${ncs.amplitude}mV (rango: 0.1-50mV)`);
    }
    
    // Validar velocidad (20-120m/s normal)
    if (ncs.velocity && (ncs.velocity < 20 || ncs.velocity > 120)) {
      warnings.push(`Velocidad fuera de rango normal: ${ncs.velocity}m/s (rango: 20-120m/s)`);
    }
    
    return { warnings };
  }

  private static validateEMGRanges(emg: any): { warnings: string[] } {
    const warnings: string[] = [];
    
    // Validar amplitud PUM (500-5000µV normal)
    if (emg.motorUnitPotentials?.amplitude) {
      const amp = emg.motorUnitPotentials.amplitude;
      if (amp < 500 || amp > 5000) {
        warnings.push(`Amplitud PUM fuera de rango: ${amp}µV (rango: 500-5000µV)`);
      }
    }
    
    // Validar duración PUM (5-25ms normal)
    if (emg.motorUnitPotentials?.duration) {
      const dur = emg.motorUnitPotentials.duration;
      if (dur < 5 || dur > 25) {
        warnings.push(`Duración PUM fuera de rango: ${dur}ms (rango: 5-25ms)`);
      }
    }
    
    return { warnings };
  }

  // ========================================
  // FUNCIÓN ELIMINADA - NO INVENTAR ESTUDIOS ESPECIALES
  // ========================================
  // La función inferSpecialStudiesFromData ha sido eliminada porque 
  // el usuario requiere que SOLO se extraigan estudios especiales 
  // que realmente aparecen en el documento, no que se inventen automáticamente.
  //
  // Política: SOLO_DATOS_REALES_NO_INVENTAR
} 